import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, AlertTriangle, Clock, CheckCircle, Eye, Loader2, ExternalLink } from 'lucide-react'
import { Card, Badge } from '../atoms'
import { cn } from '../atoms'
import { useAlert, useAcknowledgeAlert, useResolveAlert } from '../../hooks/useAlerts'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'

const statusColors: Record<string, string> = {
  OPEN: 'text-red-600 bg-red-50',
  ACKNOWLEDGED: 'text-yellow-600 bg-yellow-50',
  IN_PROGRESS: 'text-blue-600 bg-blue-50',
  RESOLVED: 'text-green-600 bg-green-50',
  FALSE_POSITIVE: 'text-gray-600 bg-gray-50',
}

const severityBadge: Record<string, 'danger' | 'warning' | 'info' | 'default'> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'default',
}

export function AlertDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: alert, isLoading } = useAlert(id!)
  const ackMutation = useAcknowledgeAlert()
  const resolveMutation = useResolveAlert()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    )
  }

  const a = alert as any
  if (!a) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-500">Alerta no encontrada</p>
        <button onClick={() => navigate('/alerts')} className="mt-2 text-sm text-primary-600 hover:underline">Volver a alertas</button>
      </div>
    )
  }

  const handleAcknowledge = async () => {
    try {
      await ackMutation.mutateAsync(a.id)
      successToast('Alerta reconocida')
    } catch { errorToast('Error al reconocer alerta') }
  }

  const handleResolve = async () => {
    try {
      await resolveMutation.mutateAsync({ id: a.id })
      successToast('Alerta resuelta')
    } catch { errorToast('Error al resolver alerta') }
  }

  return (
    <div className="w-full space-y-6">
      <button onClick={() => navigate('/alerts')} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Volver a alertas
      </button>

      <Card>
        <div className="space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className={cn(
                'p-2 rounded-lg flex-shrink-0',
                (a.severity?.name || a.severity) === 'critical' ? 'bg-red-100 text-red-600' :
                (a.severity?.name || a.severity) === 'high' ? 'bg-yellow-100 text-yellow-600' :
                'bg-blue-100 text-blue-600'
              )}>
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{a.title}</h1>
                <p className="mt-1 text-sm text-gray-600">{a.description}</p>
              </div>
            </div>
            <span className={cn('px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap', statusColors[a.status] || 'bg-gray-100')}>
              {a.status?.replace(/_/g, ' ')}
            </span>
          </div>

          <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500 font-medium">Severidad</p>
              <Badge variant={severityBadge[a.severity?.name || a.severity] || 'default'}>
                {a.severity?.label || a.severity?.name || a.severity}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Tipo</p>
              <p className="text-sm text-gray-900">{(a.type?.label || a.type?.name || a.type || '').replace(/_/g, ' ')}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Categoría</p>
              <p className="text-sm text-gray-900">{a.category?.label || a.category?.name || a.category || 'Sin categoría'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Creada</p>
              <p className="text-sm text-gray-900 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-gray-400" />
                {new Date(a.createdAt).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>

          {a.resolvedAt && (
            <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-xs text-gray-500 font-medium">Resuelta</p>
                <p className="text-sm text-gray-900 flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  {new Date(a.resolvedAt).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              {a.resolutionNotes && (
                <div>
                  <p className="text-xs text-gray-500 font-medium">Notas de resolución</p>
                  <p className="text-sm text-gray-900">{a.resolutionNotes}</p>
                </div>
              )}
            </div>
          )}

          {a.acknowledgedAt && (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 font-medium">Reconocida</p>
              <p className="text-sm text-gray-900 flex items-center gap-1">
                <Eye className="w-3.5 h-3.5 text-yellow-500" />
                {new Date(a.acknowledgedAt).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          )}

          {(a.case || a.email) && (
            <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-100">
              {a.case && (
                <button onClick={() => navigate(`/cases/${a.case.id}`)} className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium">
                  <ExternalLink className="w-4 h-4" />
                  Ver caso relacionado
                </button>
              )}
              {a.email && (
                <button onClick={() => navigate(`/emails/${a.email.id}`)} className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium">
                  <ExternalLink className="w-4 h-4" />
                  Ver email relacionado
                </button>
              )}
            </div>
          )}
        </div>
      </Card>

      {a.status !== 'RESOLVED' && a.status !== 'FALSE_POSITIVE' && (
        <div className="flex items-center gap-3">
          {a.status === 'OPEN' && (
            <button onClick={handleAcknowledge} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 border border-yellow-300 rounded-lg hover:bg-yellow-100 transition-colors">
              <Eye className="w-4 h-4" />
              Reconocer alerta
            </button>
          )}
          <button onClick={handleResolve} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-300 rounded-lg hover:bg-green-100 transition-colors">
            <CheckCircle className="w-4 h-4" />
            Resolver alerta
          </button>
        </div>
      )}
    </div>
  )
}
